import { useMemo } from "react";

import { useUserReferralInfoRequest } from "domain/referrals";
import { getBasisPoints } from "lib/numbers";
import { getByKey } from "lib/objects";
import useWallet from "lib/wallets/useWallet";
import { ContractsChainId } from "sdk/configs/chains";
import { convertTokenAddress } from "sdk/configs/tokens";
import {
  getEntryPrice,
  getNetPriceImpactDeltaUsdForDecrease,
  getPositionPnlAfterFees,
  getPositionPnlUsd,
} from "sdk/utils/positions";

import useUiFeeFactorRequest from "../fees/utils/useUiFeeFactor";
import {
  MarketInfo,
  MarketsData,
  MarketsInfoData,
  getMarketIndexName,
  getMarketPoolName,
  getMaxAllowedLeverageByMinCollateralFactor,
} from "../markets";
import { TokensData, convertToTokenAmount, convertToUsd } from "../tokens";
import { getAcceptablePriceInfo, getMarkPrice } from "../trade";
import { PositionsData, PositionsInfoData } from "./types";
import { usePositionsConstantsRequest } from "./usePositionsConstants";
import { getLeverage, getLiquidationPrice, getPositionNetValue, getPositionPendingFeesUsd } from "./utils";

export type PositionsInfoResult = {
  positionsInfoData?: PositionsInfoData;
  isLoading: boolean;
  error?: Error;
};

export function usePositionsInfoRequest(
  chainId: ContractsChainId,
  p: {
    account: string | null | undefined;
    marketsInfoData?: MarketsInfoData;
    marketsData?: MarketsData;
    tokensData?: TokensData;
    positionsData?: PositionsData;
    positionsError?: Error;
    showPnlInLeverage: boolean;
    skipLocalReferralCode?: boolean;
  }
): PositionsInfoResult {
  const {
    showPnlInLeverage,
    marketsData,
    marketsInfoData,
    tokensData,
    account,
    skipLocalReferralCode = false,
    positionsData,
    positionsError,
  } = p;

  const { signer } = useWallet();
  const { positionsConstants, error: positionsConstantsError } = usePositionsConstantsRequest(chainId);
  const { minCollateralUsd } = positionsConstants || {};
  const { error: uiFeeFactorError } = useUiFeeFactorRequest(chainId);
  const userReferralInfo = useUserReferralInfoRequest(signer, chainId, account, skipLocalReferralCode);

  const error = positionsError || positionsConstantsError || uiFeeFactorError || userReferralInfo?.error;

  return useMemo(() => {
    if (!marketsData || !tokensData || !positionsData || minCollateralUsd === undefined) {
      return {
        error,
        isLoading: true,
      };
    }

    const positionsInfoData = Object.keys(positionsData).reduce((acc: PositionsInfoData, positionKey: string) => {
      const position = getByKey(positionsData, positionKey)!;

      const marketInfo = getByKey(marketsInfoData, position.marketAddress);
      // Try marketsData first, fallback to marketInfo for synthetic markets (x10000 mode)
      const market = getByKey(marketsData, position.marketAddress) || marketInfo;

      // Get token addresses from market (or marketInfo for synthetic markets)
      const indexToken = market
        ? getByKey(tokensData, convertTokenAddress(chainId, market.indexTokenAddress, "native")) ||
          (marketInfo as MarketInfo | undefined)?.indexToken // Use indexToken from marketInfo for synthetic markets
        : undefined;
      const longToken = getByKey(tokensData, market?.longTokenAddress) ||
        (marketInfo as MarketInfo | undefined)?.longToken;
      const shortToken = getByKey(tokensData, market?.shortTokenAddress) ||
        (marketInfo as MarketInfo | undefined)?.shortToken;
      const pnlToken = position.isLong ? longToken : shortToken;
      const collateralToken = getByKey(tokensData, position.collateralTokenAddress);

      if (!market || !indexToken || !longToken || !shortToken || !pnlToken || !collateralToken) {
        // Silently skip positions that can't be parsed - this is expected when:
        // 1. Market info hasn't loaded yet (temporary state)
        // 2. Market is not in the supported markets list (normal for API positions)
        if (process.env.NODE_ENV === "development") {
          // eslint-disable-next-line no-console
          console.debug(`Position parsing skipped (market info not available)`, {
            positionKey: position.key,
            marketAddress: position.marketAddress,
            hasMarket: !!market,
            hasIndexToken: !!indexToken,
            hasLongToken: !!longToken,
            hasShortToken: !!shortToken,
            hasPnlToken: !!pnlToken,
            hasCollateralToken: !!collateralToken,
          });
        }
        return acc;
      }

      const markPrice = getMarkPrice({ prices: indexToken.prices, isLong: position.isLong, isIncrease: false });
      const collateralMinPrice = collateralToken.prices.minPrice;

      // Use entryPrice from API if available (for API positions), otherwise calculate it
      const entryPrice = position.entryPrice !== undefined
        ? position.entryPrice
        : getEntryPrice({
            sizeInTokens: position.sizeInTokens,
            sizeInUsd: position.sizeInUsd,
            indexToken,
          });

      const pendingFundingFeesUsd = convertToUsd(
        position.fundingFeeAmount,
        collateralToken.decimals,
        collateralToken.prices.minPrice
      )!;

      const pendingClaimableFundingFeesLongUsd = convertToUsd(
        position.claimableLongTokenAmount,
        longToken.decimals,
        longToken.prices.minPrice
      )!;
      const pendingClaimableFundingFeesShortUsd = convertToUsd(
        position.claimableShortTokenAmount,
        shortToken.decimals,
        shortToken.prices.minPrice
      )!;

      const pendingClaimableFundingFeesUsd = pendingClaimableFundingFeesLongUsd + pendingClaimableFundingFeesShortUsd;

      const totalPendingFeesUsd = getPositionPendingFeesUsd({
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd,
      });

      const closingFeeAmount = position.positionFeeAmount - position.traderDiscountAmount;
      const closingFeeUsd = convertToUsd(closingFeeAmount, collateralToken.decimals, collateralToken.prices.minPrice)!;
      const uiFeeUsd = convertToUsd(position.uiFeeAmount, collateralToken.decimals, collateralToken.prices.minPrice)!;

      // In x10000 mode, collateralAmount is already in USDT (6 decimals), so we need to convert it to USD (30 decimals)
      // collateralAmount is stored with 6 decimals (USDT precision), but we need USD with 30 decimals
      // So: collateralUsd = collateralAmount * 10^(30-6) = collateralAmount * 10^24
      const isX10000Mode = position.originalPositionId !== undefined;
      const collateralUsd = isX10000Mode
        ? position.collateralAmount * BigInt(10 ** 24) // Convert from 6 decimals (USDT) to 30 decimals (USD)
        : convertToUsd(position.collateralAmount, collateralToken.decimals, collateralMinPrice)!;

      const remainingCollateralUsd = collateralUsd - totalPendingFeesUsd;

      const remainingCollateralAmount = convertToTokenAmount(
        remainingCollateralUsd,
        collateralToken.decimals,
        collateralMinPrice
      )!;

      // In x10000 mode, use API returned PNL directly instead of recalculating
      // Recalculating can cause precision issues or incorrect values
      const pnl = isX10000Mode
        ? position.pnl
        : marketInfo
          ? getPositionPnlUsd({
              marketInfo: marketInfo,
              sizeInUsd: position.sizeInUsd,
              isLong: position.isLong,
              markPrice: getMarkPrice({ prices: indexToken.prices, isLong: position.isLong, isIncrease: false }),
              sizeInTokens: position.sizeInTokens,
            })
          : position.pnl;

      const pnlPercentage =
        collateralUsd !== undefined && collateralUsd != 0n ? getBasisPoints(pnl, collateralUsd) : 0n;

      const closeAcceptablePriceInfo = marketInfo
        ? getAcceptablePriceInfo({
            marketInfo,
            isIncrease: false,
            isLimit: false,
            isLong: position.isLong,
            indexPrice: getMarkPrice({ prices: indexToken.prices, isLong: position.isLong, isIncrease: false }),
            sizeDeltaUsd: position.sizeInUsd,
          })
        : undefined;

      const netPriceImapctValues =
        marketInfo && closeAcceptablePriceInfo
          ? getNetPriceImpactDeltaUsdForDecrease({
              marketInfo,
              sizeInUsd: position.sizeInUsd,
              pendingImpactAmount: position.pendingImpactAmount,
              sizeDeltaUsd: position.sizeInUsd,
              priceImpactDeltaUsd: closeAcceptablePriceInfo.priceImpactDeltaUsd,
            })
          : undefined;

      const netValue = getPositionNetValue({
        collateralUsd: collateralUsd,
        pnl,
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd: pendingFundingFeesUsd,
        closingFeeUsd,
        uiFeeUsd,
        totalPendingImpactDeltaUsd: netPriceImapctValues?.totalImpactDeltaUsd ?? 0n,
        priceImpactDiffUsd: netPriceImapctValues?.priceImpactDiffUsd ?? 0n,
      });

      const pnlAfterFees = getPositionPnlAfterFees({
        pnl,
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd: pendingFundingFeesUsd,
        closingFeeUsd,
        uiFeeUsd,
        totalPendingImpactDeltaUsd: netPriceImapctValues?.totalImpactDeltaUsd ?? 0n,
        priceImpactDiffUsd: netPriceImapctValues?.priceImpactDiffUsd ?? 0n,
      });

      const pnlAfterFeesPercentage =
        collateralUsd != 0n ? getBasisPoints(pnlAfterFees, collateralUsd + closingFeeUsd) : 0n;

      const leverage = getLeverage({
        sizeInUsd: position.sizeInUsd,
        collateralUsd: collateralUsd,
        pnl: showPnlInLeverage ? pnl : undefined,
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd: pendingFundingFeesUsd,
      });

      const leverageWithPnl = getLeverage({
        sizeInUsd: position.sizeInUsd,
        collateralUsd: collateralUsd,
        pnl,
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd: pendingFundingFeesUsd,
      });

      const leverageWithoutPnl = getLeverage({
        sizeInUsd: position.sizeInUsd,
        collateralUsd: collateralUsd,
        pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
        pendingFundingFeesUsd: pendingFundingFeesUsd,
        pnl: undefined,
      });

      const maxAllowedLeverage = marketInfo
        ? getMaxAllowedLeverageByMinCollateralFactor(marketInfo.minCollateralFactor)
        : undefined;

      const hasLowCollateral =
        (leverage !== undefined && maxAllowedLeverage !== undefined && leverage > maxAllowedLeverage) || false;

      // Use liquidationPrice from API if available (for API positions), otherwise calculate it
      const liquidationPrice = position.liquidationPrice !== undefined
        ? position.liquidationPrice
        : marketInfo
          ? getLiquidationPrice({
              marketInfo,
              collateralToken,
              pendingImpactAmount: position.pendingImpactAmount,
              sizeInUsd: position.sizeInUsd,
              sizeInTokens: position.sizeInTokens,
              collateralUsd,
              collateralAmount: position.collateralAmount,
              userReferralInfo,
              minCollateralUsd,
              pendingBorrowingFeesUsd: position.pendingBorrowingFeesUsd,
              pendingFundingFeesUsd,
              isLong: position.isLong,
            })
          : undefined;

      const indexName = getMarketIndexName({ indexToken, isSpotOnly: false });
      const poolName = getMarketPoolName({ longToken, shortToken });

      acc[positionKey] = {
        ...position,
        market,
        marketInfo,
        indexName,
        poolName,
        indexToken,
        collateralToken,
        pnlToken,
        longToken,
        shortToken,
        markPrice,
        entryPrice,
        liquidationPrice,
        collateralUsd,
        remainingCollateralUsd,
        remainingCollateralAmount,
        hasLowCollateral,
        leverage,
        leverageWithPnl,
        leverageWithoutPnl,
        pnl,
        pnlPercentage,
        pnlAfterFees,
        pnlAfterFeesPercentage,
        netValue,
        netPriceImapctDeltaUsd: netPriceImapctValues?.totalImpactDeltaUsd ?? 0n,
        priceImpactDiffUsd: netPriceImapctValues?.priceImpactDiffUsd ?? 0n,
        pendingImpactUsd: netPriceImapctValues?.proportionalPendingImpactDeltaUsd ?? 0n,
        closePriceImpactDeltaUsd: closeAcceptablePriceInfo?.priceImpactDeltaUsd ?? 0n,
        closingFeeUsd,
        uiFeeUsd,
        pendingFundingFeesUsd,
        pendingClaimableFundingFeesUsd,
      };

      return acc;
    }, {} as PositionsInfoData);

    return {
      positionsInfoData,
      isLoading: false,
      error,
    };
  }, [
    error,
    marketsData,
    tokensData,
    positionsData,
    minCollateralUsd,
    marketsInfoData,
    chainId,
    showPnlInLeverage,
    userReferralInfo,
  ]);
}
